import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { trackBeginCheckout, trackPurchase, trackAddPaymentInfo } from '../utils/analytics';
import PAYMENT_CONFIG from '../config/paymentConfig';
import { 
  MapPinIcon, 
  PlusIcon, 
  CheckCircleIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';
import { getCities, getDistricts, getNeighborhoods } from '../utils/staticLocationData';
import { getHybridCities, getHybridDistricts, getHybridNeighborhoods } from '../utils/tradresAPI';

function CheckoutPage() {
  const { cartItems, clearCart } = useCart();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [isGuestMode, setIsGuestMode] = useState(!token); // Kullanıcı giriş yapmışsa guest mode kapalı
  const [guestInfo, setGuestInfo] = useState({
    email: '',
    phone: '',
    full_name: ''
  });

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [showAddressConfirmation, setShowAddressConfirmation] = useState(false);
  const [showAddressSaveModal, setShowAddressSaveModal] = useState(false);
  const [pendingNewAddress, setPendingNewAddress] = useState(null);
  const [currentUserAddress, setCurrentUserAddress] = useState(null);
  const [shippingDetails, setShippingDetails] = useState({
    full_name: '',
    address_line: '',
    district: '',
    city: '',
    neighborhood: '',
    postal_code: '',
    phone: '',
    address_title: '',
    country: 'Türkiye'
  });
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(true);
  const [loading, setLoading] = useState(false);
  
  // Kargo seçimi için state'ler
  const [shippingProviders, setShippingProviders] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [showShippingOptions, setShowShippingOptions] = useState(false);

  // Kupon sistemi için state'ler
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);

  // Sepet kampanyası için state'ler
  const [cartDiscounts, setCartDiscounts] = useState([]);
  const [cartDiscountLoading, setCartDiscountLoading] = useState(false);
  
  // Kategori kampanyası için state'ler
  const [categoryDiscounts, setCategoryDiscounts] = useState([]);
  const [categoryDiscountLoading, setCategoryDiscountLoading] = useState(false);
  
  // Özel gün kampanyası için state'ler
  const [specialDayDiscounts, setSpecialDayDiscounts] = useState([]);
  const [specialDayLoading, setSpecialDayLoading] = useState(false);
  
  // Flash Sale kampanyası için state'ler
  const [flashSaleDiscounts, setFlashSaleDiscounts] = useState([]);
  const [flashSaleLoading, setFlashSaleLoading] = useState(false);
  
  // Paket kampanyası için state'ler
  const [packageDeals, setPackageDeals] = useState([]);
  const [packageDealsLoading, setPackageDealsLoading] = useState(false);
  
  // Ödeme yöntemi için state'ler
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  useEffect(() => {
    const fetchUserAddresses = async () => {
      if (!token || isGuestMode) {
        setAddressesLoading(false);
        setShowNewAddressForm(true);
        return;
      }
      
      setAddressesLoading(true);
      try {
        const data = await api('/api/addresses', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const deliveryAddresses = data.filter(addr => addr.address_type === 'delivery');
        setAddresses(deliveryAddresses);
        
        if (deliveryAddresses.length > 0) {
          // Kullanıcının kayıtlı adresi var
          const primaryAddress = deliveryAddresses[0];
          setCurrentUserAddress(primaryAddress);
          setSelectedAddressId(primaryAddress.id);
          
          // Mevcut adres bilgilerini checkout formuna yükle
          setShippingDetails({
            full_name: primaryAddress.full_name || '',
            address_line: primaryAddress.address_line || '',
            district: primaryAddress.district || '',
            city: primaryAddress.city || '',
            neighborhood: primaryAddress.neighborhood || '',
            postal_code: primaryAddress.postal_code || '',
            phone: primaryAddress.phone || '',
            address_title: primaryAddress.address_title || '',
            country: primaryAddress.country || 'Türkiye'
          });
          
          console.log('✅ Kullanıcının kayıtlı adresi yüklendi:', primaryAddress.address_title);
        } else {
          // Kullanıcının kayıtlı adresi yok
          setShowNewAddressForm(false); // Varsayılan olarak kapalı, kullanıcı isterse açacak
          console.log('ℹ️ Kullanıcının kayıtlı adresi bulunamadı');
        }
      } catch (error) {
        console.error("Adresler yüklenirken hata:", error);
        toast.error("Adresler yüklenirken hata: " + (error.message || 'Bilinmeyen hata'));
        setShowNewAddressForm(false); // Varsayılan olarak kapalı
      } finally {
        setAddressesLoading(false);
      }
    };
    
    fetchUserAddresses();
  }, [token, isGuestMode]);

  useEffect(() => {
    if (isGuestMode) {
      setShowNewAddressForm(true); // Misafir modunda açık olmalı çünkü başka seçenek yok
      setSelectedAddressId(null);
    }
  }, [isGuestMode]);

  useEffect(() => {
    if (!token) {
      setIsGuestMode(true);
      setAddressesLoading(false);
    } else {
      setIsGuestMode(false);
    }
  }, [token]);

  useEffect(() => {
    const getProvinces = async () => {
      setLoadingProvinces(true);
      try {
        // Hızlı Tradres API ile hibrit yaklaşım
        const provincesData = await getHybridCities(getCities);
        
        // Şehirleri özel sıralama ile düzenle: İstanbul, Ankara, İzmir önce
        const sortedProvinces = sortCitiesByPriority(provincesData);
        setProvinces(sortedProvinces);
        
        // Default olarak İstanbul'u seç ve ilçelerini yükle
        await setDefaultIstanbul(sortedProvinces);
        
        console.log('✅ Şehirler başarıyla yüklendi ve İstanbul default seçildi');
      } catch (error) {
        if (error.message !== 'Timeout') {
          console.warn('⚠️ Şehir verileri yüklenirken sorun:', error.message);
        }
        // Son çare olarak statik veriyi dene
        try {
          const staticCities = await getCities();
          const sortedCities = sortCitiesByPriority(staticCities);
          setProvinces(sortedCities);
          await setDefaultIstanbul(sortedCities);
          console.log('📋 Statik şehir verisi kullanılıyor');
        } catch (staticError) {
          console.error('❌ Hiçbir şehir verisi yüklenemedi:', staticError);
          toast.error('Şehir verileri yüklenemedi. Lütfen sayfayı yenileyin.');
        }
      } finally {
        setLoadingProvinces(false);
      }
    };
    getProvinces();
  }, []);

  // Kargo seçeneklerini yükle ve GA4 begin_checkout eventi gönder
  // Ödeme yöntemlerini yükle
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoadingPaymentMethods(true);
        const response = await api('/api/payment-methods/active');
        
        if (response.success && response.data) {
          setPaymentMethods(response.data);
          // Varsayılan yöntemi seç
          const defaultMethod = response.data.find(m => m.is_default) || response.data[0];
          setSelectedPaymentMethod(defaultMethod);
        }
      } catch (error) {
        console.error('Ödeme yöntemleri yüklenirken hata:', error);
        toast.error('Ödeme yöntemleri yüklenemedi');
      } finally {
        setLoadingPaymentMethods(false);
      }
    };
    
    fetchPaymentMethods();
  }, []);
  
  useEffect(() => {
    if (cartItems.length > 0 && (selectedAddressId || showNewAddressForm)) {
      loadShippingOptions();
      
      // GA4: Checkout başlatıldı
      const subtotal = cartItems.reduce((total, item) => {
        const price = item.variant?.price_override || item.price;
        return total + (price * item.quantity);
      }, 0);
      
      trackBeginCheckout(
        cartItems.map(item => ({
          product: {
            id: item.id,
            name: item.name,
            price: item.variant?.price_override || item.price,
            category: item.category
          },
          quantity: item.quantity,
          selected_size: item.variant?.size || item.variant?.color || ''
        })),
        subtotal
      );
    }
  }, [cartItems, selectedAddressId, shippingDetails.city, shippingDetails.district]);

  // Kampanyaları kontrol et (sepet değiştiğinde)
  useEffect(() => {
    checkCartDiscounts();
    checkCategoryDiscounts();
    checkPackageDeals(); // Paket kampanyası kontrolü eklendi
    checkSpecialDayDiscounts();
    checkFlashSaleDiscounts();
  }, [cartItems]);

  // Adres değiştiğinde de kampanyaları yeniden kontrol et
  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      console.log('📍 Adres değişti, kampanyalar yeniden kontrol ediliyor...');
      checkCartDiscounts();
      checkCategoryDiscounts();
      checkPackageDeals(); // Paket kampanyası kontrolü eklendi
      checkSpecialDayDiscounts();
      checkFlashSaleDiscounts();
    }
  }, [shippingDetails.city, shippingDetails.district]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingDetails(prev => ({ ...prev, [name]: value }));
  };

  // Gelişmiş kargo seçeneklerini yükle
  const loadShippingOptions = async () => {
    setLoadingShipping(true);
    try {
      // Adres bilgilerini hazırla
      let addressData;
      
      if (selectedAddressId && !showNewAddressForm) {
        // Kayıtlı adres seçildi
        const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
        if (!selectedAddress) {
          throw new Error('Seçili adres bulunamadı');
        }
        addressData = {
          full_name: selectedAddress.full_name,
          address_line: selectedAddress.address_line,
          city: selectedAddress.city,
          district: selectedAddress.district,
          phone: selectedAddress.phone,
          email: user?.email || 'musteri@minelakids.com'
        };
      } else if (showNewAddressForm || isGuestMode) {
        // Yeni adres formu
        if (!shippingDetails.city || !shippingDetails.district) {
          console.log('[Checkout] ⚠️ Adres bilgileri eksik, kargo hesaplaması yapılamıyor');
          return;
        }
        addressData = {
          full_name: isGuestMode ? guestInfo.full_name : shippingDetails.full_name,
          address_line: shippingDetails.address_line,
          city: shippingDetails.city,
          district: shippingDetails.district,
          phone: shippingDetails.phone,
          email: isGuestMode ? guestInfo.email : (user?.email || 'musteri@minelakids.com')
        };
      } else {
        console.log('[Checkout] ⚠️ Adres seçimi yapılmamış');
        return;
      }

      console.log('[Checkout] 🚀 Kargo hesaplaması başlatılıyor:', {
        city: addressData.city,
        district: addressData.district,
        cartItemsCount: cartItems.length
      });

      // Gelişmiş kargo hesaplama API'si
      const response = await api.post('/api/shipping-calculator/calculate', {
        shippingAddress: addressData,
        cartItems: cartItems
      });

      console.log('[Checkout] 📦 Kargo API yanıtı:', {
        success: response.success,
        optionsCount: response.data?.shippingOptions?.length || 0,
        source: response.data?.source || 'unknown'
      });

      if (response.success && response.data.shippingOptions.length > 0) {
        const options = response.data.shippingOptions;
        setShippingProviders(options);
        
        // Default seçimi yap (en ucuz veya ücretsiz)
        const defaultOption = response.data.defaultSelected || options[0];
        setSelectedShipping(defaultOption);
        
        console.log(`[Checkout] ✅ ${options.length} kargo seçeneği yüklendi`);
        console.log(`[Checkout] 🎯 Varsayılan seçim: ${defaultOption.name} - ₺${defaultOption.price}`);
        
        // Uyarı mesajı varsa göster
        if (response.data.warning) {
          toast.warning(response.data.warning, {
            position: "top-center",
            autoClose: 5000,
          });
        }
        
        // Başarı mesajı (sadece Kargonomi'den gelirse)
        if (response.data.source === 'kargonomi' && options.length > 1) {
          toast.success(`${options.length} kargo seçeneği bulundu! En uygun fiyat seçildi.`, {
            position: "top-center",
            autoClose: 3000,
          });
        }
      } else {
        throw new Error('Kargo seçeneği bulunamadı');
      }
    } catch (error) {
      console.error('[Checkout] ❌ Kargo seçenekleri yüklenirken hata:', error);
      
      // Hata durumunda güvenli varsayılan kargo
      const cartTotal = calculateItemsTotal();
      const freeShippingLimit = 2000;
      const defaultPrice = cartTotal >= freeShippingLimit ? 0 : 15.00;
      
      const fallbackProviders = [{
        id: 'fallback_default',
        name: 'Standart Kargo',
        price: defaultPrice,
        originalPrice: 15.00,
        estimatedDays: '2-3',
        description: 'Güvenli teslimat',
        isFreeShipping: cartTotal >= freeShippingLimit,
        freeShippingLimit: freeShippingLimit,
        isDefault: true,
        logo: null
      }];
      
      setShippingProviders(fallbackProviders);
      setSelectedShipping(fallbackProviders[0]);
      
      // Kullanıcı dostu hata mesajı
      toast.error('Kargo fiyatları şu anda alınamıyor. Standart kargo ücreti gösteriliyor.', {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setLoadingShipping(false);
    }
  };

  // Şehirleri öncelik sırasına göre sırala: İstanbul, Ankara, İzmir önce
  const sortCitiesByPriority = (cities) => {
    const priorityCities = ['İSTANBUL', 'ANKARA', 'İZMİR'];
    const priority = [];
    const others = [];
    
    cities.forEach(city => {
      const cityName = city.name.toUpperCase();
      const priorityIndex = priorityCities.indexOf(cityName);
      if (priorityIndex !== -1) {
        priority[priorityIndex] = city;
      } else {
        others.push(city);
      }
    });
    
    // Boş slotları filtrele ve diğerlerini alfabetik sırala
    const sortedPriority = priority.filter(Boolean);
    const sortedOthers = others.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    
    return [...sortedPriority, ...sortedOthers];
  };

  // Default olarak İstanbul'u seç ve ilçelerini yükle
  const setDefaultIstanbul = async (cities) => {
    const istanbul = cities.find(city => city.name.toUpperCase() === 'İSTANBUL');
    if (istanbul && (!shippingDetails.city || shippingDetails.city === '')) {
      console.log('🏙️ Default olarak İstanbul seçiliyor...');
      
      // İstanbul'u seç
      setShippingDetails(prev => ({ 
        ...prev, 
        city: istanbul.name,
        district: '',
        neighborhood: ''
      }));
      
      try {
        // İstanbul'un ilçelerini yükle
        const districtsData = await getHybridDistricts(istanbul.name, getDistricts, null);
        setDistricts(districtsData);
        console.log(`✅ İstanbul ilçeleri default yüklendi (${districtsData.length} adet)`);
        
        // İlk ilçeyi seç (genelde Adalar, Arnavutköy vs. gelir ama Kadıköy'ü tercih edelim)
        const kadikoy = districtsData.find(d => d.name.toUpperCase().includes('KADIKOY'));
        const firstDistrict = kadikoy || districtsData[0];
        
        if (firstDistrict) {
          setShippingDetails(prev => ({ 
            ...prev, 
            district: firstDistrict.name,
            neighborhood: ''
          }));
          
          try {
            // İlk ilçenin mahallelerini yükle
            const neighborhoodsData = await getHybridNeighborhoods(istanbul.name, firstDistrict.name, getNeighborhoods, null);
            setNeighborhoods(neighborhoodsData);
            console.log(`✅ ${firstDistrict.name} mahalleleri default yüklendi (${neighborhoodsData.length} adet)`);
          } catch (error) {
            console.warn('⚠️ Default mahalle yüklenirken sorun:', error.message);
          }
        }
      } catch (error) {
        console.warn('⚠️ Default ilçe yüklenirken sorun:', error.message);
      }
    }
  };

  const handleProvinceChange = async (e) => {
    const provinceName = e.target.value;
    setShippingDetails(prev => ({ ...prev, city: provinceName, district: '', neighborhood: '' }));
    setDistricts([]);
    setNeighborhoods([]);
    
    if (provinceName) {
      try {
        console.log(`🔄 ${provinceName} ilçeleri yükleniyor...`);
        // Doğrudan Tradres API'yi kullan
        const districtsData = await getHybridDistricts(provinceName, getDistricts, null);
        setDistricts(districtsData);
        console.log(`✅ ${provinceName} ilçeleri yüklendi (${districtsData.length} adet)`);
      } catch (error) {
        console.warn(`⚠️ ${provinceName} ilçeleri yüklenirken sorun:`, error.message);
        // Hata durumunda boş array bırak, kullanıcı manuel girebilir
        setDistricts([]);
      }
    }
  };

  const handleDistrictChange = async (e) => {
    const districtName = e.target.value;
    setShippingDetails(prev => ({ ...prev, district: districtName, neighborhood: '' }));
    setNeighborhoods([]);
    
    if (districtName) {
      try {
        console.log(`🔄 ${shippingDetails.city}/${districtName} mahalleleri yükleniyor...`);
        // Doğrudan Tradres API'yi kullan
        const cityName = shippingDetails.city;
        const neighborhoodsData = await getHybridNeighborhoods(cityName, districtName, getNeighborhoods, null);
        setNeighborhoods(neighborhoodsData);
        console.log(`✅ ${districtName} mahalleleri yüklendi (${neighborhoodsData.length} adet)`);
      } catch (error) {
        console.warn(`⚠️ ${districtName} mahalleleri yüklenirken sorun:`, error.message);
        // Mahalle zorunlu değil, boş bırakılabilir
        setNeighborhoods([]);
      }
    }
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
    setShowNewAddressForm(false);
  };

  const handleNewAddress = () => {
    setShowNewAddressForm(!showNewAddressForm); // Toggle yaparak aç/kapat
    if (!showNewAddressForm) {
      setSelectedAddressId(null); // Sadece açarken selectedAddressId'yi temizle
    }
  };

  const saveNewAddress = async () => {
    try {
      const newAddress = {
        ...shippingDetails,
        address_type: 'delivery'
      };
      
      const savedAddress = await api('/api/addresses', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newAddress)
      });
      
      setAddresses(prev => [...prev, savedAddress]);
      setSelectedAddressId(savedAddress.id);
      setShowNewAddressForm(false);
      toast.success('Adres kaydedildi!');
    } catch (error) {
      toast.error('Adres kaydedilemedi: ' + error.message);
    }
  };

  // Adres kaydetme handler'ı
  const handleSaveAddress = async (updatePermanent) => {
    try {
      if (updatePermanent && token) {
        // Mevcut teslimat adresini güncelle
        const response = await api.post('/api/addresses', shippingDetails, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.success) {
          toast.success('✅ Teslimat adresiniz başarıyla güncellendi!');
          
          // Adres listesini yenile
          const newAddress = response.data;
          setAddresses(prev => [...prev, newAddress]);
          setSelectedAddressId(newAddress.id);
          setCurrentUserAddress(newAddress);
          
          // Formu kapat
          setShowNewAddressForm(false);
        }
      } else {
        toast.success('✅ Adres bu sipariş için kaydedildi!');
        // "Sadece bu sipariş için" seçeneği için showNewAddressForm'u açık bırak
        // Bu sayede siparişi tamamla butonu aktif kalır
      }
      
      // Modal'ı kapat
      setShowAddressSaveModal(false);
      
    } catch (error) {
      console.error('Adres kaydetme hatası:', error);
      toast.error('❌ Adres kaydedilirken bir hata oluştu');
    }
  };

  const calculateTotalPrice = () => {
    const itemsTotal = calculateItemsTotal();
    const couponDiscount = calculateCouponDiscount();
    const cartDiscount = calculateCartDiscount();
    const categoryDiscount = calculateCategoryDiscount();
    const packageDiscount = calculatePackageDiscount(); // Paket kampanyası indirimi eklendi
    const specialDayDiscount = calculateSpecialDayDiscount();
    const flashSaleDisc = calculateFlashSaleDiscount();
    const totalDiscounts = couponDiscount + cartDiscount + categoryDiscount + packageDiscount + specialDayDiscount + flashSaleDisc;
    const discountedItemsTotal = itemsTotal - totalDiscounts;
    
    // Seçili kargo ücreti
    const shippingCost = selectedShipping ? (selectedShipping.price || 0) : 0;
    return discountedItemsTotal + shippingCost;
  };

  const calculateItemsTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.variant.price_override != null ? item.variant.price_override : item.price;
      return total + price * item.quantity;
    }, 0);
  };

  // Kupon uygulama fonksiyonu
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Lütfen bir kupon kodu girin');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const cartTotal = calculateItemsTotal();
      const response = await api.post('/api/campaigns/apply-coupon', {
        couponCode: couponCode.trim().toUpperCase(),
        cartTotal: cartTotal,
        cartItems: cartItems
      });

      if (response.success) {
        setAppliedCoupon(response.data);
        setCouponCode('');
        setShowCouponInput(false);
        toast.success(`🎉 Kupon uygulandı! ₺${response.data.discountAmount.toFixed(2)} indirim kazandınız!`);
      } else {
        setCouponError(response.message || 'Kupon uygulanamadı');
        toast.error(response.message || 'Kupon uygulanamadı');
      }
    } catch (error) {
      console.error('Kupon uygulama hatası:', error);
      setCouponError(error.message || 'Kupon kontrol edilirken bir hata oluştu');
      toast.error('Kupon kontrol edilirken bir hata oluştu');
    } finally {
      setCouponLoading(false);
    }
  };

  // Kupon kaldırma fonksiyonu
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
    toast.info('Kupon kaldırıldı');
  };

  // Kupon indirimi hesaplama
  const calculateCouponDiscount = () => {
    if (!appliedCoupon) return 0;
    
    const cartTotal = calculateItemsTotal();
    let discount = 0;

    if (appliedCoupon.discountType === 'percentage') {
      discount = (cartTotal * appliedCoupon.discountValue) / 100;
      if (appliedCoupon.maxDiscountAmount && discount > appliedCoupon.maxDiscountAmount) {
        discount = appliedCoupon.maxDiscountAmount;
      }
    } else if (appliedCoupon.discountType === 'fixed_amount') {
      discount = appliedCoupon.discountValue;
    }

    return Math.min(discount, cartTotal); // İndirim sepet toplamını geçemez
  };

  // Sepet kampanyalarını kontrol et
  const checkCartDiscounts = async () => {
    if (!cartItems || cartItems.length === 0) {
      setCartDiscounts([]);
      return;
    }

    // Sepet toplamını hesapla
    const cartTotal = calculateItemsTotal();

    console.log('🛒 Checkout: Sepet kampanyası kontrol ediliyor:', {
      cartTotal: cartTotal,
      itemCount: cartItems.length
    });

    setCartDiscountLoading(true);
    try {
      const response = await api.post('/api/campaigns/check-cart-discounts', {
        cartTotal: cartTotal,
        cartItems: cartItems
      });

      console.log('📥 Checkout: Sepet kampanyası API yanıtı:', response);

      if (response.success && response.data.hasDiscounts) {
        setCartDiscounts(response.data.applicableDiscounts);
        console.log('✅ Checkout: Sepet kampanyaları bulundu:', response.data.applicableDiscounts.length);
      } else {
        console.log('❌ Checkout: Sepet kampanyası bulunamadı');
        setCartDiscounts([]);
      }
    } catch (error) {
      console.error('❌ Checkout: Sepet kampanyası kontrol hatası:', error);
      setCartDiscounts([]);
    } finally {
      setCartDiscountLoading(false);
    }
  };

  // Sepet kampanyalarının toplam tutarını hesapla
  const calculateCartDiscount = () => {
    if (!cartDiscounts || cartDiscounts.length === 0) return 0;
    return cartDiscounts.reduce((total, discount) => {
      const amount = discount.discountAmount || discount.totalCampaignDiscount || 0;
      return total + amount;
    }, 0);
  };

  // Kategori kampanyalarını kontrol et
  const checkCategoryDiscounts = async () => {
    if (!cartItems || cartItems.length === 0) {
      setCategoryDiscounts([]);
      return;
    }

    console.log('🏷️ Checkout: Kategori kampanyası kontrol ediliyor:', {
      itemCount: cartItems.length
    });

    setCategoryDiscountLoading(true);
    try {
      const response = await api.post('/api/campaigns/check-unified-category-discounts', {
        cartItems: cartItems
      });

      console.log('📥 Checkout: Kategori kampanyası API yanıtı:', response);

      if (response.success && response.data.hasDiscounts && response.data.applicableDiscounts) {
        // Her kampanya için gerekli alanları normalize et
        const normalizedDiscounts = response.data.applicableDiscounts.map(discount => ({
          ...discount,
          discountAmount: discount.discountAmount || discount.totalCampaignDiscount || 0,
          campaignName: discount.campaignName || discount.name || 'Kategori İndirimi',
          badgeText: discount.badgeText || `%${discount.discountValue || 0}`,
          badgeColor: discount.badgeColor || '#8b5cf6'
        }));
        
        setCategoryDiscounts(normalizedDiscounts);
        console.log('✅ Checkout: Kategori kampanyaları bulundu:', normalizedDiscounts.length, normalizedDiscounts);
      } else {
        console.log('❌ Checkout: Kategori kampanyası bulunamadı');
        setCategoryDiscounts([]);
      }
    } catch (error) {
      console.error('❌ Checkout: Kategori kampanyası kontrol hatası:', error);
      setCategoryDiscounts([]);
    } finally {
      setCategoryDiscountLoading(false);
    }
  };

  // Kategori kampanyalarının toplam tutarını hesapla
  const calculateCategoryDiscount = () => {
    if (!categoryDiscounts || categoryDiscounts.length === 0) return 0;
    return categoryDiscounts.reduce((total, discount) => total + (discount.discountAmount || 0), 0);
  };

  // Paket kampanyalarını kontrol et
  const checkPackageDeals = async () => {
    if (!cartItems || cartItems.length === 0) {
      setPackageDeals([]);
      return;
    }

    console.log('📦 Checkout: Paket kampanyası kontrol ediliyor:', {
      itemCount: cartItems.length,
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity
      }))
    });

    setPackageDealsLoading(true);
    try {
      const response = await api.post('/api/campaigns/check-package-deals', {
        cartItems: cartItems
      });

      console.log('📥 Checkout: Paket kampanyası API yanıtı:', response);

      if (response.success && response.data.hasDiscounts) {
        setPackageDeals(response.data.applicableDiscounts);
        console.log('✅ Checkout: Paket kampanyaları bulundu:', response.data.applicableDiscounts.length);
      } else {
        console.log('❌ Checkout: Paket kampanyası bulunamadı');
        setPackageDeals([]);
      }
    } catch (error) {
      console.error('❌ Checkout: Paket kampanyası kontrol hatası:', error);
      setPackageDeals([]);
    } finally {
      setPackageDealsLoading(false);
    }
  };

  // Paket kampanyalarının toplam tutarını hesapla
  const calculatePackageDiscount = () => {
    if (!packageDeals || packageDeals.length === 0) return 0;
    return packageDeals.reduce((total, deal) => total + deal.discountAmount, 0);
  };

  // Özel gün kampanyalarını kontrol et
  const checkSpecialDayDiscounts = async () => {
    if (!cartItems || cartItems.length === 0) {
      setSpecialDayDiscounts([]);
      return;
    }

    const cartTotal = calculateItemsTotal();

    console.log('🎉 Checkout: Özel gün kampanyası kontrol ediliyor:', {
      cartTotal: cartTotal,
      itemCount: cartItems.length
    });

    setSpecialDayLoading(true);
    try {
      const response = await api.post('/api/campaigns/check-special-day-discounts', {
        cartItems: cartItems,
        cartTotal: cartTotal,
        userInfo: null // Kullanıcı bilgisi varsa buraya eklenebilir
      });

      console.log('📥 Checkout: Özel gün kampanyası API yanıtı:', response);

      if (response.success && response.data.hasDiscounts) {
        setSpecialDayDiscounts(response.data.applicableDiscounts);
        console.log('✅ Checkout: Özel gün kampanyaları bulundu:', response.data.applicableDiscounts.length);
      } else {
        console.log('❌ Checkout: Özel gün kampanyası bulunamadı');
        setSpecialDayDiscounts([]);
      }
    } catch (error) {
      console.error('❌ Checkout: Özel gün kampanyası kontrol hatası:', error);
      setSpecialDayDiscounts([]);
    } finally {
      setSpecialDayLoading(false);
    }
  };

  // Özel gün kampanyalarının toplam tutarını hesapla
  const calculateSpecialDayDiscount = () => {
    if (!specialDayDiscounts || specialDayDiscounts.length === 0) return 0;
    return specialDayDiscounts.reduce((total, discount) => total + discount.discountAmount, 0);
  };

  // Flash Sale kontrolü
  const checkFlashSaleDiscounts = async () => {
    if (!cartItems || cartItems.length === 0) {
      setFlashSaleDiscounts([]);
      return;
    }

    console.log('⚡ Checkout: Flash Sale kontrol ediliyor:', cartItems.length, 'ürün');
    setFlashSaleLoading(true);

    try {
      const response = await api.get('/api/flash-sale/active');
      
      if (response.success && response.data && response.data.length > 0) {
        const applicableDiscounts = [];
        
        for (const flashSale of response.data) {
          if (!flashSale.products || flashSale.products.length === 0) continue;
          
          const matchingItems = cartItems.filter(cartItem => 
            flashSale.products.some(p => String(p.id) === String(cartItem.id))
          );
          
          if (matchingItems.length > 0) {
            let totalDiscount = 0;
            
            matchingItems.forEach(cartItem => {
              const productInSale = flashSale.products.find(p => p.id === cartItem.id);
              const discountValue = productInSale.custom_discount_value || flashSale.discount_value;
              const itemPrice = cartItem.variant?.price_override || cartItem.price;
              
              let itemDiscount = 0;
              if (flashSale.discount_type === 'percentage') {
                itemDiscount = (itemPrice * discountValue) / 100;
              } else {
                itemDiscount = discountValue;
              }
              
              totalDiscount += itemDiscount * cartItem.quantity;
            });
            
            applicableDiscounts.push({
              campaignId: flashSale.id,
              campaignName: flashSale.name,
              badgeText: flashSale.badge_text || `FLASH %${flashSale.discount_value}`,
              badgeColor: flashSale.badge_color || '#ff4444',
              discountAmount: totalDiscount,
              discountType: flashSale.discount_type,
              discountValue: flashSale.discount_value,
              matchingItems: matchingItems.length
            });
          }
        }
        
        setFlashSaleDiscounts(applicableDiscounts);
        console.log('✅ Checkout: Flash Sale indirimleri bulundu:', applicableDiscounts.length);
      } else {
        setFlashSaleDiscounts([]);
      }
    } catch (error) {
      console.error('❌ Checkout: Flash Sale kontrol hatası:', error);
      setFlashSaleDiscounts([]);
    } finally {
      setFlashSaleLoading(false);
    }
  };

  // Flash Sale indirimlerinin toplam tutarını hesapla
  const calculateFlashSaleDiscount = () => {
    if (!flashSaleDiscounts || flashSaleDiscounts.length === 0) return 0;
    return flashSaleDiscounts.reduce((total, discount) => total + discount.discountAmount, 0);
  };

  // Sepetteki ürünlerin toplam desisini hesapla
  const calculateCartDesi = () => {
    if (!cartItems || cartItems.length === 0) return 1;
    
    let totalDesi = 0;
    cartItems.forEach(item => {
      // Ürünün desi bilgisi varsa onu kullan, yoksa varsayılan 0.5
      const itemDesi = item.desi || 0.5;
      totalDesi += (item.quantity || 1) * itemDesi;
    });
    
    return Math.max(1, Math.ceil(totalDesi)); // Minimum 1 desi
  };

  // Kargo şirketi için desi bazlı fiyat hesapla
  const calculateShippingPrice = (provider, desi, settings = {}) => {
    // Ana depo şehri (İstanbul)
    const MAIN_DEPOT_CITY = 'İSTANBUL';
    
    // Teslimat şehrini normalize et - kayıtlı adres veya form adresinden al
    let deliveryCity = '';
    
    if (selectedAddressId && !showNewAddressForm) {
      // Kayıtlı adres seçildi
      const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
      deliveryCity = selectedAddress?.city?.toUpperCase().trim() || '';
    } else {
      // Yeni adres formu
      deliveryCity = shippingDetails?.city?.toUpperCase().trim() || '';
    }
    
    // Şehir içi mi şehir dışı mı kontrol et
    const isLocalDelivery = deliveryCity === MAIN_DEPOT_CITY;
    
    // Uygun fiyat listesini seç
    const desiPrices = isLocalDelivery 
      ? (provider.desi_prices_local || {})
      : (provider.desi_prices_intercity || {});
    
    console.log(`[Checkout] ${provider.name} - ${deliveryCity} ${isLocalDelivery ? '(Şehir İçi)' : '(Şehir Dışı)'} - ${desi} desi`);
    
    // Belirtilen desi için fiyat var mı kontrol et
    if (desiPrices[desi] && desiPrices[desi] !== '') {
      const price = parseFloat(desiPrices[desi]);
      console.log(`[Checkout] Desi fiyatı bulundu: ${price} TL`);
      return price;
    }

    // Yoksa temel fiyatı kullan
    const basePrice = provider.base_price || 15.0;
    console.log(`[Checkout] Temel fiyat kullanılıyor: ${basePrice} TL`);
    return basePrice;
  };
  const totalPrice = calculateTotalPrice();

  // Render modals to avoid deep inline JSX causing parse issues
  const renderAddressConfirmationModal = () => {
    if (!showAddressConfirmation) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
          <h3 className="text-lg font-bold mb-4 text-gray-800">Adres Güncelleme Onayı</h3>
          <div className="mb-4">
            <p className="text-gray-600 mb-3">
              Mevcut kayıtlı adresiniz var. Yeni girdiğiniz adres bilgileri ile hesabınızdaki adresi güncellemek ister misiniz?
            </p>
            <div className="bg-gray-50 p-3 rounded mb-3">
              <h4 className="font-semibold text-sm text-gray-700 mb-2">Mevcut Adresiniz:</h4>
              <p className="text-sm text-gray-600">
                {currentUserAddress?.address_title} - {currentUserAddress?.city}, {currentUserAddress?.district}
              </p>
              <p className="text-sm text-gray-600">{currentUserAddress?.address_line}</p>
            </div>
            <div className="bg-blue-50 p-3 rounded">
              <h4 className="font-semibold text-sm text-gray-700 mb-2">Yeni Adres:</h4>
              <p className="text-sm text-gray-600">
                {shippingDetails.city}, {shippingDetails.district}, {shippingDetails.neighborhood}
              </p>
              <p className="text-sm text-gray-600">{shippingDetails.address_line}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => handleNewAddressConfirmation(true)}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
            >
              Evet, Adresimi Güncelle
            </button>
            <button
              onClick={() => handleNewAddressConfirmation(false)}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition-colors"
            >
              Hayır, Sadece Bu Kargo İçin
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddressSaveModal = () => {
    if (!showAddressSaveModal) return null;
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Overlay */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-60 transition-opacity duration-300"
          onClick={() => setShowAddressSaveModal(false)}
        ></div>
        {/* Modal */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Adres Kaydetme Seçeneği</h3>
                  <p className="text-sm text-gray-500">Bu adresi nasıl kaydetmek istiyorsunuz?</p>
                </div>
              </div>
            </div>
            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                <button
                  onClick={() => handleSaveAddress(true)}
                  className="w-full p-4 text-left border-2 border-green-200 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all duration-200 group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200">
                      <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Mevcut Teslimat Adresimi Değiştir</h4>
                      <p className="text-sm text-gray-600">Bu adresi hesabıma kaydet ve gelecekte kullan</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => handleSaveAddress(false)}
                  className="w-full p-4 text-left border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200">
                      <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Sadece Bu Sipariş İçin</h4>
                      <p className="text-sm text-gray-600">Bu adresi kaydetme, sadece bu siparişte kullan</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            {/* Footer */}
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowAddressSaveModal(false)}
                className="w-full py-3 px-4 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Yeni adres onay fonksiyonu
  const handleNewAddressConfirmation = async (confirmed) => {
    if (confirmed) {
      // Kullanıcı mevcut adresini güncellemeyi kabul etti
      try {
        const updatedAddress = await api(`/api/addresses/${currentUserAddress.id}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...pendingNewAddress,
            address_type: 'delivery'
          })
        });
        setCurrentUserAddress(updatedAddress);
        setAddresses(prev => prev.map(addr => 
          addr.id === currentUserAddress.id ? updatedAddress : addr
        ));
        toast.success("Adres bilgileriniz güncellendi");
      } catch (error) {
        toast.error("Adres güncellenirken hata oluştu");
      }
    } else {
      // Kullanıcı mevcut adresini korumak istiyor, yeni adresi geçici olarak kullan
      toast.info("Mevcut adresiniz korundu, yeni adres sadece bu sipariş için kullanılacak");
    }
    setShowAddressConfirmation(false);
    setPendingNewAddress(null);
    
    // Siparişi tamamla
    await completeOrder();
  };

  const completeOrderAndPay = async () => {
    setLoading(true);
    setPaymentProcessing(true);

    let finalShippingDetails;

    if (showAddressConfirmation && pendingNewAddress) {
      // Onay modalından gelen yeni adres
      finalShippingDetails = pendingNewAddress;
    } else if (showNewAddressForm || isGuestMode) {
      // Yeni adres formu veya misafir modu
      if (isGuestMode) {
        finalShippingDetails = { 
          ...shippingDetails, 
          full_name: guestInfo.full_name 
        };
      } else {
        finalShippingDetails = shippingDetails;
      }
    } else if (selectedAddressId) {
      // Mevcut adres seçildi
      const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
      if (selectedAddress) {
        finalShippingDetails = {
          full_name: selectedAddress.full_name,
          address_line: selectedAddress.address_line,
          city: selectedAddress.city,
          phone: selectedAddress.phone,
          district: selectedAddress.district,
          neighborhood: selectedAddress.neighborhood,
          postal_code: selectedAddress.postal_code,
          address_title: selectedAddress.address_title,
          country: selectedAddress.country || 'Türkiye'
        };
      }
    }

    if (!finalShippingDetails) {
      toast.error("Adres bilgileri bulunamadı");
      setLoading(false);
      return;
    }

    const orderPayload = {
      shippingDetails: finalShippingDetails,
      items: cartItems,
      totalPrice: totalPrice,
      isGuestOrder: isGuestMode,
      guestInfo: isGuestMode ? guestInfo : null,
      selectedShipping: selectedShipping,
      appliedCoupon: appliedCoupon,
      couponDiscount: calculateCouponDiscount(),
      cartDiscounts: cartDiscounts,
      cartDiscount: calculateCartDiscount(),
      specialDayDiscounts: specialDayDiscounts,
      specialDayDiscount: calculateSpecialDayDiscount(),
      packageDeals: packageDeals,
      packageDiscount: calculatePackageDiscount()
    };

    try {
      const headers = isGuestMode ? {} : { 'Authorization': `Bearer ${token}` };
      const payloadWithFlash = {
        ...orderPayload,
        flashSaleDiscounts: flashSaleDiscounts,
        flashSaleDiscount: calculateFlashSaleDiscount()
      };
      const orderResponse = await api('/api/orders', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payloadWithFlash)
      });
      
      const orderId = orderResponse.orderId || orderResponse.id;
      console.log('✅ Sipariş oluşturuldu:', orderId);
      
      // Ödeme checkout başlat
      const checkoutPayload = {
        orderId: orderId,
        userId: user?.id || null,
        paymentProvider: selectedPaymentMethod.slug
      };
      
      const checkoutResponse = await api.post('/api/payment/checkout', checkoutPayload, {
        headers: headers
      });
      
      if (checkoutResponse.success && checkoutResponse.paymentPageUrl) {
        // GA4: Ödeme bilgisi eklendi
        trackAddPaymentInfo(
          cartItems.map(item => ({
            product: {
              id: item.id,
              name: item.name,
              price: item.variant?.price_override || item.price,
              category: item.category
            },
            quantity: item.quantity,
            selected_size: item.variant?.size || item.variant?.color || ''
          })),
          totalPrice,
          selectedPaymentMethod.slug
        );
        
        toast.success('Ödeme sayfasına yönlendiriliyorsunuz...');
        
        // Sepeti temizle
        clearCart();
        
        // İyzico ödeme sayfasına yönlendir
        window.location.href = checkoutResponse.paymentPageUrl;
      } else {
        throw new Error(checkoutResponse.error || 'Ödeme sayfası oluşturulamadı');
      }
    } catch (error) {
      console.error('❌ Sipariş/Ödeme hatası:', error);
      toast.error(error.message || 'Sipariş oluşturulurken hata oluştu');
    } finally {
      setLoading(false);
      setPaymentProcessing(false);
    }
  };
  
  // Eski completeOrder fonksiyonu - modal onay için kullanılıyor
  const completeOrder = async () => {
    return completeOrderAndPay();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isGuestMode) {
      if (!guestInfo.email || !guestInfo.phone || !guestInfo.full_name) {
        toast.error('Lütfen tüm iletişim bilgilerini doldurun.');
        return;
      }
      if (!guestInfo.email.includes('@')) {
        toast.error('Lütfen geçerli bir email adresi girin.');
        return;
      }
    } else if (!token) {
      toast.error('Lütfen giriş yapın veya misafir olarak devam edin.');
      return;
    }
    if (cartItems.length === 0) {
      toast.error("Sepetinizde ürün bulunmuyor.");
      return;
    }
    
    if (!selectedPaymentMethod) {
      toast.error("Ödeme yöntemi seçilmedi.");
      return;
    }

    if (showNewAddressForm || isGuestMode) {
      const nameField = isGuestMode ? guestInfo.full_name : shippingDetails.full_name;
      if (!nameField || !shippingDetails.address_line || !shippingDetails.city || !shippingDetails.phone) {
        toast.error("Lütfen tüm adres bilgilerini doldurun.");
        return;
      }
      
      if (!isGuestMode) {
        // Kullanıcının mevcut adresi var mı kontrol et
        if (currentUserAddress) {
          // Adres değişikliği var mı kontrol et
          const isAddressDifferent = 
            currentUserAddress.city !== shippingDetails.city ||
            currentUserAddress.district !== shippingDetails.district ||
            currentUserAddress.neighborhood !== shippingDetails.neighborhood ||
            currentUserAddress.address_line !== shippingDetails.address_line ||
            currentUserAddress.phone !== shippingDetails.phone;

          if (isAddressDifferent) {
            // Kullanıcıya onay sor
            setPendingNewAddress({
              ...shippingDetails,
              address_type: 'delivery'
            });
            setShowAddressConfirmation(true);
            return;
          }
        } else {
          // İlk kez adres giriyor, kaydet
          await saveNewAddress();
        }
      }
    } else if (!selectedAddressId) {
      toast.error("Lütfen bir teslimat adresi seçin veya yeni adres ekleyin.");
      return;
    }

    await completeOrderAndPay();
  };

  if (addressesLoading) {
    return (
      <div className="min-h-screen bg-brand-header flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 mx-auto mb-4"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-green border-t-transparent absolute top-0 left-1/2 transform -translate-x-1/2"></div>
          </div>
          <div className="animate-pulse">
            <p className="text-text-dark font-medium">Adresler yükleniyor...</p>
            <p className="text-text-light text-sm mt-1">Lütfen bekleyin</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-header">
      {/* Payment Test Mode Banner - Controlled by paymentConfig.js */}
      {PAYMENT_CONFIG.IS_TEST_MODE && (
        <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 text-white py-3 px-4 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-center space-x-3">
            <svg className="w-6 h-6 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-semibold text-lg">TEST MODU - Gerçek para çekilmeyecek</span>
            <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium">Sandbox</span>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green rounded-full mb-4 shadow-lg">
            <TruckIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-3 tracking-tight">Siparişi Tamamla</h1>
          <p className="text-text-light text-lg max-w-md mx-auto">Son adım! Teslimat bilgilerinizi kontrol edin ve siparişinizi tamamlayın</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center space-x-6">
              <div className="flex items-center group">
                <div className="relative">
                  <div className="w-12 h-12 bg-brand-green-light rounded-full flex items-center justify-center shadow-md transition-all duration-300 group-hover:scale-110">
                    <CheckCircleIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <span className="ml-3 text-sm font-semibold text-brand-green-light">Sepet</span>
              </div>
              <div className="w-12 h-1 bg-gradient-to-r from-brand-green to-brand-green-light rounded-full"></div>
              <div className="flex items-center group">
                <div className="relative">
                  <div className="w-12 h-12 bg-brand-green rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 animate-pulse">
                    <TruckIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-5 h-5 bg-brand-green rounded-full border-2 border-white animate-ping"></div>
                </div>
                <span className="ml-3 text-sm font-bold text-brand-green">Teslimat</span>
              </div>
              <div className="w-12 h-1 bg-gray-200 rounded-full"></div>
              <div className="flex items-center group">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-105">
                  <CreditCardIcon className="w-6 h-6 text-gray-400" />
                </div>
                <span className="ml-3 text-sm font-medium text-gray-400">Ödeme</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Teslimat Adresi Seçimi */}
          <div className="lg:col-span-2">
            
            {!token && (
              <div className="bg-white rounded-3xl shadow-xl p-8 mb-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-text-dark">Sipariş Türü</h2>
                    <p className="text-text-light text-sm">Üye olarak veya misafir olarak sipariş verebilirsiniz</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      if (!token) {
                        navigate('/auth');
                      } else {
                        setIsGuestMode(false);
                      }
                    }}
                    className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                      !isGuestMode && token
                        ? 'border-brand-green bg-gradient-to-br from-green-50 to-emerald-50'
                        : 'border-gray-300 hover:border-brand-green-light bg-white hover:bg-green-50'
                    }`}
                  >
                    <div className="text-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                        !isGuestMode && token ? 'bg-brand-green' : 'bg-gray-400'
                      }`}>
                        <span className="text-white text-xl">👤</span>
                      </div>
                      <h3 className="font-bold text-gray-800 mb-2">
                        {token ? 'Üye Olarak Sipariş Ver' : 'Giriş Yap / Üye Ol'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {token ? 'Kayıtlı adreslerinizi kullanın' : 'Siparişlerinizi takip edin, hızlı alışveriş yapın'}
                      </p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setIsGuestMode(true)}
                    className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                      isGuestMode 
                        ? 'border-brand-green bg-gradient-to-br from-green-50 to-emerald-50' 
                        : 'border-gray-300 hover:border-brand-green-light bg-white hover:bg-green-50'
                    }`}
                  >
                    <div className="text-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                        isGuestMode ? 'bg-brand-green' : 'bg-gray-400'
                      }`}>
                        <span className="text-white text-xl">🛒</span>
                      </div>
                      <h3 className="font-bold text-gray-800 mb-2">Misafir Olarak Sipariş Ver</h3>
                      <p className="text-sm text-gray-600">Üye olmadan hızlıca sipariş verin</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {isGuestMode && (
              <div className="bg-white rounded-3xl shadow-xl p-8 mb-6 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg mr-4">
                    <span className="text-white text-xl">📧</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-text-dark">İletişim Bilgileri</h2>
                    <p className="text-text-light text-sm">Sipariş durumu için iletişim bilgilerinizi girin</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ad Soyad *</label>
                    <input
                      type="text"
                      value={guestInfo.full_name}
                      onChange={(e) => setGuestInfo(prev => ({ ...prev, full_name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Adresi *</label>
                    <input
                      type="email"
                      value={guestInfo.email}
                      onChange={(e) => setGuestInfo(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="ornek@email.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefon Numarası *</label>
                    <input
                      type="tel"
                      value={guestInfo.phone}
                      onChange={(e) => setGuestInfo(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                      placeholder="0555 123 45 67"
                    />
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">💡 Bilgi:</span> Bu bilgiler sadece sipariş durumu hakkında sizi bilgilendirmek için kullanılacaktır.
                  </p>
                </div>
              </div>
            )}
            <div className="bg-white rounded-3xl shadow-xl p-8 mb-6 border border-gray-100 backdrop-blur-sm">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-green to-brand-green-light rounded-xl flex items-center justify-center shadow-lg mr-4">
                  <MapPinIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-text-dark">Teslimat Adresi</h2>
                  <p className="text-text-light text-sm">Siparişinizin teslim edileceği adresi seçin</p>
                </div>
              </div>

              {!isGuestMode && addresses.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Kayıtlı Adreslerim</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {addresses.map(address => (
                      <div
                        key={address.id}
                        onClick={() => handleAddressSelect(address.id)}
                        className={`group p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                          selectedAddressId === address.id
                            ? 'border-brand-green bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg ring-4 ring-brand-green ring-opacity-20'
                            : 'border-gray-200 hover:border-brand-green-light hover:shadow-lg bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <div className={`w-3 h-3 rounded-full mr-2 ${selectedAddressId === address.id ? 'bg-brand-green' : 'bg-gray-300'}`}></div>
                              <h4 className="font-bold text-gray-800 text-lg">{address.address_title}</h4>
                            </div>
                            <p className="text-base text-gray-700 font-medium mb-1">{address.full_name}</p>
                            <p className="text-sm text-gray-600 leading-relaxed">{address.address_line}</p>
                            <p className="text-sm text-gray-600">{address.district}, {address.city}</p>
                            <div className="flex items-center mt-2">
                              <div className="w-4 h-4 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                                <span className="text-xs">📞</span>
                              </div>
                              <p className="text-sm text-gray-600 font-medium">{address.phone}</p>
                            </div>
                          </div>
                          {selectedAddressId === address.id && (
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-brand-green rounded-full flex items-center justify-center shadow-lg animate-bounce">
                                <CheckCircleIcon className="w-5 h-5 text-white" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isGuestMode && (
                <button
                  onClick={handleNewAddress}
                  className={`w-full p-4 rounded-xl border-2 border-dashed transition-all duration-200 ${
                    showNewAddressForm
                      ? 'border-brand-green bg-green-50'
                      : 'border-gray-300 hover:border-brand-green-light hover:bg-green-50'
                  }`}
                >
                  <div className="flex items-center justify-center">
                    {showNewAddressForm ? (
                      <>
                        <svg className="w-5 h-5 text-brand-green mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span className="text-brand-green font-medium">Formu Kapat</span>
                      </>
                    ) : (
                      <>
                        <PlusIcon className="w-5 h-5 text-brand-green mr-2" />
                        <span className="text-brand-green font-medium">Yeni Adres Ekle</span>
                      </>
                    )}
                  </div>
                </button>
              )}

              {/* Animasyonlu Yeni Adres Formu */}
              <div className={`mt-6 overflow-hidden transition-all duration-500 ease-in-out transform ${
                showNewAddressForm || isGuestMode 
                  ? 'max-h-[800px] opacity-100 scale-y-100 translate-y-0' 
                  : 'max-h-0 opacity-0 scale-y-0 -translate-y-2'
              }`}>
                {(showNewAddressForm || isGuestMode) && (
                  <div className={`p-4 md:p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-brand-green border-opacity-30 shadow-lg transition-all duration-300 ${
                    showNewAddressForm || isGuestMode ? 'transform scale-100' : 'transform scale-95'
                  }`}>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-brand-green to-brand-green-light rounded-xl flex items-center justify-center shadow-lg mr-3">
                          <MapPinIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">
                            {isGuestMode ? 'Teslimat Adresi' : 'Yeni Adres Bilgileri'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {isGuestMode ? 'Siparişinizin teslim edileceği adresi girin' : 'Yeni adres bilgilerinizi ekleyin'}
                          </p>
                        </div>
                      </div>
                      {!isGuestMode && (
                        <button
                          onClick={() => setShowNewAddressForm(false)}
                          className="p-2 rounded-lg bg-red-100 hover:bg-red-200 transition-all duration-300 transform hover:scale-110"
                        >
                          <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    {!isGuestMode && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Adres Başlığı</label>
                        <input
                          type="text"
                          name="address_title"
                          value={shippingDetails.address_title}
                          onChange={handleInputChange}
                          placeholder="Ev, İş, vb."
                          className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200"
                        />
                      </div>
                    )}
                    <div className={isGuestMode ? "md:col-span-2" : ""}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ad Soyad *</label>
                      <input
                        type="text"
                        name="full_name"
                        value={isGuestMode ? guestInfo.full_name : shippingDetails.full_name}
                        onChange={isGuestMode ? 
                          (e) => setGuestInfo(prev => ({ ...prev, full_name: e.target.value })) : 
                          handleInputChange
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Adres</label>
                      <textarea
                        name="address_line"
                        value={shippingDetails.address_line}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">İl *</label>
                      <select
                        name="city"
                        value={shippingDetails.city}
                        onChange={handleProvinceChange}
                        required
                        className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200"
                      >
                        <option value="">{loadingProvinces ? 'İller yükleniyor...' : 'İl Seçiniz'}</option>
                        {provinces.map(province => (
                          <option key={province.id} value={province.name}>
                            {province.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">İlçe *</label>
                      <select
                        name="district"
                        value={shippingDetails.district}
                        onChange={handleDistrictChange}
                        required
                        disabled={!shippingDetails.city || districts.length === 0}
                        className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200 disabled:bg-gray-100"
                      >
                        <option value="">{districts.length === 0 ? 'İlçe Seçiniz' : 'İlçe Seçiniz'}</option>
                        {districts.map(district => (
                          <option key={district.id} value={district.name}>
                            {district.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mahalle</label>
                      <select
                        name="neighborhood"
                        value={shippingDetails.neighborhood}
                        onChange={handleInputChange}
                        disabled={!shippingDetails.district || neighborhoods.length === 0}
                        className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200 disabled:bg-gray-100"
                      >
                        <option value="">Mahalle Seçiniz</option>
                        {neighborhoods.map(neighborhood => (
                          <option key={neighborhood.id} value={neighborhood.name}>
                            {neighborhood.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Posta Kodu</label>
                      <input
                        type="text"
                        name="postal_code"
                        value={shippingDetails.postal_code}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                      <input
                        type="tel"
                        name="phone"
                        value={shippingDetails.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green transition-all duration-200"
                      />
                    </div>
                    
                    {/* Kaydet Butonu - Sadece üye kullanıcılar için */}
                    {!isGuestMode && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => setShowAddressSaveModal(true)}
                          className="w-full bg-gradient-to-r from-brand-green to-brand-green-light text-white py-3 md:py-4 px-4 md:px-6 rounded-xl font-semibold hover:from-brand-green-light hover:to-brand-green transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] text-sm md:text-base"
                        >
                          <span className="flex items-center justify-center">
                            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Adresi Kaydet
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Sipariş Özeti */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-2xl p-8 sticky top-8 border border-gray-100 backdrop-blur-sm">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-green to-brand-green-light rounded-xl flex items-center justify-center shadow-lg mr-4">
                  <ShoppingBagIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-text-dark">Sipariş Özeti</h2>
                  <p className="text-text-light text-sm">{cartItems.length} ürün</p>
                </div>
              </div>

              <div className="space-y-6 mb-8">
                {cartItems.map((item, index) => (
                  <div key={item.cartId} className="group flex items-center space-x-4 p-4 rounded-2xl transition-all duration-300 hover:bg-gray-50 hover:shadow-md">
                    <div className="relative">
                      <img
                        src={item.main_image_url || 'https://placehold.co/80x80'}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-2xl flex-shrink-0 shadow-lg transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-brand-green rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-xs font-bold">{item.quantity}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 text-sm mb-1 group-hover:text-brand-green transition-colors">{item.name}</h4>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="inline-block w-3 h-3 rounded-full border-2 border-gray-300" style={{backgroundColor: item.variant.color?.toLowerCase()}}></span>
                        <p className="text-xs text-gray-600 font-medium">{item.variant.color}</p>
                        <span className="text-gray-400">•</span>
                        <p className="text-xs text-gray-600 font-medium">{item.variant.size}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-brand-green text-lg">
                        ₺{((item.variant.price_override != null ? item.variant.price_override : item.price) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t-2 border-gray-100 pt-6 space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-700 font-medium">Ara Toplam</span>
                  <span className="text-gray-800 font-bold">₺{calculateItemsTotal().toFixed(2)}</span>
                </div>

                {/* Kategori Kampanyasi (varsa) */}
                {categoryDiscounts && categoryDiscounts.length > 0 && (
                  <div className="p-3 bg-purple-50 rounded-xl">
                    <div className="flex justify-between items-center">
                      <span className="text-purple-700 font-medium flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: categoryDiscounts[0].badgeColor || '#8b5cf6'}}></span>
                        Kategori Kampanyasi
                      </span>
                      <span className="text-purple-700 font-bold">
                        -₺{categoryDiscounts.reduce((s, d) => s + (d.discountAmount || 0), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="mt-2 space-y-1">
                      {categoryDiscounts.map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-xs text-gray-700">
                          <div className="flex items-center gap-2">
                            {d.badgeText && (
                              <span className="px-2 py-0.5 rounded-full text-white" style={{backgroundColor: d.badgeColor || '#8b5cf6'}}>
                                {d.badgeText}
                              </span>
                            )}
                            <span>{d.campaignName || 'Kategori indirimi'}</span>
                          </div>
                          <span className="font-semibold">-₺{(d.discountAmount || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </div>

                {/* Kupon Bölümü */}
                <div className="relative">
                  {!appliedCoupon ? (
                    <div className={`transition-all duration-300 ${showCouponInput ? 'mb-4' : ''}`}>
                      <button
                        onClick={() => setShowCouponInput(!showCouponInput)}
                        className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-brand-green hover:border-brand-green-light hover:bg-green-50 transition-all duration-300 group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-brand-green to-brand-green-light rounded-lg flex items-center justify-center shadow-lg">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <span className="text-brand-green font-semibold">
                            {showCouponInput ? 'Kupon Kodunu Gizle' : 'Kupon Kodunuz Var mı?'}
                          </span>
                        </div>
                        <div className={`transform transition-transform duration-300 ${showCouponInput ? 'rotate-180' : ''}`}>
                          <svg className="w-5 h-5 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/* Kupon Giriş Formu */}
                      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        showCouponInput 
                          ? 'max-h-40 opacity-100 mt-4' 
                          : 'max-h-0 opacity-0'
                      }`}>
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                          <div className="flex space-x-3">
                            <div className="flex-1">
                              <input
                                type="text"
                                value={couponCode}
                                onChange={(e) => {
                                  setCouponCode(e.target.value.toUpperCase());
                                  setCouponError('');
                                }}
                                onKeyPress={(e) => e.key === 'Enter' && applyCoupon()}
                                placeholder="KUPON KODUNU GİRİN"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-center font-mono font-bold tracking-wider"
                                disabled={couponLoading}
                              />
                              {couponError && (
                                <p className="text-red-500 text-xs mt-2 text-center">{couponError}</p>
                              )}
                            </div>
                            <button
                              onClick={applyCoupon}
                              disabled={couponLoading || !couponCode.trim()}
                              className="px-6 py-3 bg-gradient-to-r from-brand-green to-brand-green-light text-white rounded-lg font-semibold hover:from-brand-green-light hover:to-brand-green transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105"
                            >
                              {couponLoading ? (
                                <div className="flex items-center space-x-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  <span className="text-sm">Kontrol</span>
                                </div>
                              ) : (
                                'Uygula'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Uygulanan Kupon */
                    <div className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300 p-4 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center shadow-lg">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-green-800 font-bold text-sm">
                              {appliedCoupon.couponCode} Uygulandı
                            </p>
                            <p className="text-green-600 text-xs">
                              {appliedCoupon.discountType === 'percentage' 
                                ? `%${appliedCoupon.discountValue} indirim`
                                : `₺${appliedCoupon.discountValue} indirim`
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-green-700 font-bold">
                            -₺{calculateCouponDiscount().toFixed(2)}
                          </span>
                          <button
                            onClick={removeCoupon}
                            className="p-1 rounded-full bg-red-100 hover:bg-red-200 transition-colors"
                          >
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                )

                {/* Sepet Kampanyaları Bölümü */}
                {cartDiscounts.length > 0 && (
                  <div className="relative">
                    <div className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300 p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center shadow-lg">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-green-800 font-bold text-sm">
                              Sepet Kampanyası Uygulandı
                            </p>
                            <p className="text-green-600 text-xs">
                              {cartDiscounts.length} kampanya aktif
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-green-700 font-bold">
                            -₺{calculateCartDiscount().toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Sepet Kampanyası Detayları */}
                      <div className="space-y-2">
                        {cartDiscounts.map((discount, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-3 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                {discount.badgeText && (
                                  <span 
                                    className="px-2 py-1 text-xs font-bold text-white rounded-full"
                                    style={{ backgroundColor: discount.badgeColor || '#10b981' }}
                                  >
                                    {discount.badgeText}
                                  </span>
                                )}
                                <span className="text-sm font-medium text-gray-800">
                                  {discount.campaignName}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-green-700">
                                -₺{discount.discountAmount.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Kampanya Açıklaması */}
                            {discount.description && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600">{discount.description}</p>
                              </div>
                            )}
                            
                            {/* Minimum/Maksimum Sepet Bilgisi */}
                            <div className="mt-2 text-xs text-gray-500">
                              {discount.minCartAmount && (
                                <span>Min: ₺{discount.minCartAmount.toFixed(2)}</span>
                              )}
                              {discount.maxCartAmount && (
                                <span className="ml-2">Max: ₺{discount.maxCartAmount.toFixed(2)}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Paket Kampanyaları Bölümü */}
                {packageDeals.length > 0 && (
                  <div className="relative">
                    <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center shadow-lg">
                            <span className="text-white text-sm font-bold">📦</span>
                          </div>
                          <div>
                            <p className="text-purple-800 font-bold text-sm">
                              Paket Kampanyası Uygulandı
                            </p>
                            <p className="text-purple-600 text-xs">
                              {packageDeals.length} kampanya aktif
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-purple-700 font-bold">
                            -₺{calculatePackageDiscount().toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Paket Kampanyası Detayları */}
                      <div className="space-y-2">
                        {packageDeals.map((deal, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-3 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                {deal.badgeText && (
                                  <span 
                                    className="px-2 py-1 text-xs font-bold text-white rounded-full"
                                    style={{ backgroundColor: deal.badgeColor || '#8b5cf6' }}
                                  >
                                    {deal.badgeText}
                                  </span>
                                )}
                                <span className="text-sm font-medium text-gray-800">
                                  {deal.campaignName}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-purple-700">
                                -₺{deal.discountAmount.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Kampanya Açıklaması */}
                            {deal.description && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600">{deal.description}</p>
                              </div>
                            )}
                            
                            {/* Etkilenen Ürünler */}
                            {deal.eligibleItems && deal.eligibleItems.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {deal.eligibleItems.map((item, itemIndex) => (
                                  <div key={itemIndex} className="flex justify-between text-xs text-gray-600">
                                    <span>
                                      {item.productName}
                                      {item.variantInfo && ` (${item.variantInfo})`}
                                      {item.freeItems && ` - ${item.freeItems} adet bedava`}
                                    </span>
                                    <span>-₺{item.itemDiscount.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Flash Sale Kampanyaları Bölümü */}
                {flashSaleDiscounts.length > 0 && (
                  <div className="relative">
                    <div className="bg-gradient-to-r from-red-100 to-orange-100 border-2 border-red-300 p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center shadow-lg animate-pulse">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-red-800 font-bold text-sm">
                              ⚡ Flash Sale İndirimi Uygulandı
                            </p>
                            <p className="text-red-600 text-xs">
                              {flashSaleDiscounts.length} kampanya aktif
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-red-700 font-bold">
                            -₺{calculateFlashSaleDiscount().toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {flashSaleDiscounts.map((discount, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-3 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span 
                                  className="px-2 py-1 text-xs font-bold text-white rounded-full"
                                  style={{ backgroundColor: discount.badgeColor || '#ff4444' }}
                                >
                                  ⚡ {discount.badgeText}
                                </span>
                                <span className="text-sm font-medium text-gray-800">
                                  {discount.campaignName}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-red-700">
                                -₺{discount.discountAmount.toFixed(2)}
                              </span>
                            </div>
                            <div className="mt-2 text-xs text-gray-500 flex justify-between">
                              <span>{discount.matchingItems} ürün</span>
                              {discount.discountType === 'percentage' && (
                                <span>%{discount.discountValue} indirim</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Özel Gün Kampanyaları Bölümü */}
                {specialDayDiscounts.length > 0 && (
                  <div className="relative">
                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center shadow-lg animate-bounce">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-purple-800 font-bold text-sm">
                              🎉 Özel Gün Kampanyası Uygulandı
                            </p>
                            <p className="text-purple-600 text-xs">
                              {specialDayDiscounts.length} kampanya aktif
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-purple-700 font-bold">
                            -₺{calculateSpecialDayDiscount().toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Özel Gün Kampanyası Detayları */}
                      <div className="space-y-2">
                        {specialDayDiscounts.map((discount, index) => (
                          <div key={index} className="bg-white bg-opacity-60 p-3 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span 
                                  className="px-2 py-1 text-xs font-bold text-white rounded-full animate-pulse"
                                  style={{ backgroundColor: discount.badgeColor || '#8b5cf6' }}
                                >
                                  🎉 {discount.badgeText}
                                </span>
                                <span className="text-sm font-medium text-gray-800">
                                  {discount.campaignName}
                                </span>
                              </div>
                              <span className="text-sm font-bold text-purple-700">
                                -₺{discount.discountAmount.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Özel Gün ve Hedef Kitle Bilgisi */}
                            <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                              <span>
                                {(() => {
                                  const audienceNames = {
                                    'all': 'Tüm müşteriler',
                                    'new_customers': 'Yeni müşteriler',
                                    'returning_customers': 'Sadık müşteriler',
                                    'vip_customers': 'VIP müşteriler'
                                  };
                                  return audienceNames[discount.targetAudience] || 'Tüm müşteriler';
                                })()}
                              </span>
                              <span className="text-purple-600 font-medium">
                                {(() => {
                                  const dayNames = {
                                    'mothers_day': 'Anneler Günü',
                                    'fathers_day': 'Babalar Günü',
                                    'childrens_day': '23 Nisan Çocuklar Günü',
                                    'new_year': 'Yeni Yıl',
                                    'valentines_day': 'Sevgililer Günü',
                                    'back_to_school': 'Okula Dönüş',
                                    'semester_break': 'Sömestr Tatili',
                                    'summer_vacation': 'Yaz Tatili',
                                    'report_card_day': 'Karne Günü',
                                    'custom_date': 'Özel Gün'
                                  };
                                  return dayNames[discount.specialDayType] || 'Özel Gün';
                                })()}
                              </span>
                            </div>
                            
                            {/* Kampanya Açıklaması */}
                            {discount.description && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600">{discount.description}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Kompakt Kargo Seçimi */}
                <div className="relative">
                  <div 
                    className={`flex justify-between items-center p-4 rounded-xl transition-all duration-300 ${
                      loadingShipping 
                        ? 'bg-gray-50 border border-gray-200 cursor-wait' 
                        : selectedShipping?.isFreeShipping 
                          ? 'bg-green-50 border border-green-200 cursor-pointer hover:shadow-md' 
                          : 'bg-orange-50 border border-orange-200 cursor-pointer hover:shadow-md'
                    }`}
                    onClick={() => !loadingShipping && setShowShippingOptions(!showShippingOptions)}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-700 font-medium">Kargo</span>
                      
                      {/* Kargo Şirketi Logosu */}
                      {selectedShipping?.logo ? (
                        <img 
                          src={selectedShipping.logo} 
                          alt={selectedShipping.name}
                          className="h-6 w-auto max-w-[60px] object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : (
                        <div className="w-6 h-6 bg-gray-300 rounded flex items-center justify-center">
                          <TruckIcon className="w-4 h-4 text-gray-600" />
                        </div>
                      )}
                      
                      {/* Kargo Şirketi Adı */}
                      <span className="text-sm text-gray-600 font-medium">
                        {loadingShipping ? 'Hesaplanıyor...' : (selectedShipping?.name || 'Kargo seçin')}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Fiyat */}
                      <div className="text-right">
                        {loadingShipping ? (
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                            <span className="text-gray-500 font-medium">Hesaplanıyor</span>
                          </div>
                        ) : selectedShipping?.isFreeShipping ? (
                          <span className="text-green-600 font-bold">Ücretsiz</span>
                        ) : (
                          <span className="text-orange-600 font-bold">₺{(selectedShipping?.price || 0).toFixed(2)}</span>
                        )}
                        {!loadingShipping && selectedShipping?.estimatedDays && (
                          <p className="text-xs text-gray-500">{selectedShipping.estimatedDays} iş günü</p>
                        )}
                      </div>
                      
                      {/* Açılır Ok */}
                      <div className={`transform transition-transform duration-300 ${showShippingOptions ? 'rotate-180' : ''}`}>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Yukarıya Açılan Kargo Seçenekleri */}
                  <div className={`absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 transform origin-bottom ${
                    showShippingOptions 
                      ? 'opacity-100 scale-y-100 translate-y-0' 
                      : 'opacity-0 scale-y-0 translate-y-4 pointer-events-none'
                  }`}>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                        <TruckIcon className="w-4 h-4 mr-2 text-brand-green" />
                        Kargo Şirketi Seçin
                      </h4>
                      
                      {loadingShipping ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-green border-t-transparent mx-auto"></div>
                          <p className="text-xs text-gray-500 mt-2">Yükleniyor...</p>
                        </div>
                      ) : shippingProviders.length === 0 ? (
                        <p className="text-center text-gray-500 text-sm py-4">Kargo seçeneği bulunamadı</p>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {shippingProviders.map((provider, index) => {
                            const isSelected = selectedShipping?.id === provider.id;
                            const isCheapest = index === 0;
                            
                            return (
                              <div
                                key={provider.id}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                                  isSelected
                                    ? 'bg-brand-green bg-opacity-10 border border-brand-green'
                                    : 'hover:bg-gray-50 border border-transparent'
                                }`}
                                onClick={() => {
                                  setSelectedShipping(provider);
                                  setShowShippingOptions(false);
                                }}
                              >
                                <div className="flex items-center space-x-3">
                                  {/* Radio Button */}
                                  <div className={`w-4 h-4 rounded-full border-2 transition-all ${
                                    isSelected ? 'border-brand-green bg-brand-green' : 'border-gray-300'
                                  }`}>
                                    {isSelected && (
                                      <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                                    )}
                                  </div>
                                  
                                  {/* Logo */}
                                  {provider.logo ? (
                                    <img 
                                      src={provider.logo} 
                                      alt={provider.name}
                                      className="h-5 w-auto max-w-[50px] object-contain"
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  ) : (
                                    <div className="w-5 h-5 bg-gray-200 rounded flex items-center justify-center">
                                      <TruckIcon className="w-3 h-3 text-gray-500" />
                                    </div>
                                  )}
                                  
                                  {/* Bilgiler */}
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium text-gray-800">{provider.name}</span>
                                      {isCheapest && !provider.isFreeShipping && (
                                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">
                                          En Ucuz
                                        </span>
                                      )}
                                      {provider.isFreeShipping && (
                                        <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                                          Ücretsiz
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500">{provider.estimatedDays} iş günü</p>
                                  </div>
                                </div>
                                
                                {/* Fiyat */}
                                <div className="text-right">
                                  {provider.isFreeShipping ? (
                                    <span className="text-sm font-bold text-green-600">Ücretsiz</span>
                                  ) : (
                                    <span className="text-sm font-bold text-gray-800">₺{provider.price.toFixed(2)}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Ücretsiz Kargo Bilgilendirmesi */}
                {selectedShipping && !selectedShipping.isFreeShipping && (
                  <div className="bg-gradient-to-r from-orange-100 to-amber-100 border border-orange-200 p-4 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <TruckIcon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-orange-800">
                          {(() => {
                            const currentTotal = calculateItemsTotal();
                            const freeShippingLimit = selectedShipping.freeShippingLimit || 2000;
                            const remaining = freeShippingLimit - currentTotal;
                            
                            if (remaining > 0) {
                              return `₺${remaining.toFixed(2)} daha ürün eklerseniz ücretsiz kargo!`;
                            }
                            return 'Ücretsiz kargo hakkınız var!';
                          })()}
                        </p>
                        <div className="mt-2 bg-orange-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500"
                            style={{
                              width: `${Math.min(100, (calculateItemsTotal() / (selectedShipping.freeShippingLimit || 2000)) * 100)}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ödeme Yöntemi Seçimi */}
                <div className="border-t-2 border-gray-100 pt-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg mr-3">
                      <CreditCardIcon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Ödeme Yöntemi</h3>
                  </div>
                  
                  {loadingPaymentMethods ? (
                    <div className="flex items-center justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green"></div>
                    </div>
                  ) : paymentMethods.length === 0 ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
                      <p className="text-red-700 text-sm">Henüz aktif ödeme yöntemi bulunmuyor</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentMethods.map((method) => {
                        const isSelected = selectedPaymentMethod?.id === method.id;
                        return (
                          <div
                            key={method.id}
                            onClick={() => setSelectedPaymentMethod(method)}
                            className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                              isSelected
                                ? 'border-brand-green bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg'
                                : 'border-gray-200 hover:border-brand-green-light hover:shadow-md bg-white'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  isSelected ? 'border-brand-green bg-brand-green' : 'border-gray-300'
                                }`}>
                                  {isSelected && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                                <div className="text-2xl">💳</div>
                                <div>
                                  <h4 className="font-bold text-gray-800 text-sm">{method.display_name}</h4>
                                  <p className="text-xs text-gray-500">{method.description}</p>
                                </div>
                              </div>
                              {method.is_test_mode && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                                  Test Modu
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-4 rounded-2xl shadow-lg">
                  <div className="flex justify-between items-center text-white">
                    <span className="text-lg font-bold">Toplam</span>
                    <span className="text-2xl font-bold">₺{totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={
                  loading || 
                  cartItems.length === 0 || 
                  (!selectedAddressId && !showNewAddressForm && !isGuestMode) || 
                  (showNewAddressForm && !shippingDetails.full_name) || 
                  (showNewAddressForm && !shippingDetails.address_line) || 
                  (showNewAddressForm && !shippingDetails.city) || 
                  (showNewAddressForm && !shippingDetails.phone) ||
                  loadingShipping || 
                  !selectedShipping ||
                  (isGuestMode && (!guestInfo.email || !guestInfo.phone || !guestInfo.full_name))
                }
                className="w-full mt-8 bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-5 px-8 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sipariş Veriliyor...
                  </div>
                ) : loadingShipping ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Kargo Ücretleri Hesaplanıyor...
                  </div>
                ) : paymentProcessing ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Ödeme Sayfası Hazırlanıyor...
                  </div>
                ) : !selectedShipping ? (
                  'Kargo Seçimi Yapın'
                ) : !selectedPaymentMethod ? (
                  'Ödeme Yöntemi Seçin'
                ) : (
                  <div className="flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Güvenli Ödemeye Geç
                  </div>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Siparişinizi tamamlayarak <span className="text-brand-green">Gizlilik Politikası</span> ve <span className="text-brand-green">Kullanım Şartları</span>'nı kabul etmiş olursunuz.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Adres Onay Modalı */}
      {renderAddressConfirmationModal()}

      {/* Adres Kaydetme Onay Modalı */}
      {renderAddressSaveModal()}
    </div>
  );
};

export default CheckoutPage;